import type { ListingsAPIResponseBody } from '@noths/polaris-client-listings-api';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { adaptResponseDynamicFacetsToStore } from 'src/adapters/serviceResponseToStore/listings-api/adaptResponseDynamicFacetsToStore';
import { adaptResponseFacetsToStore } from 'src/adapters/serviceResponseToStore/listings-api/adaptResponseFacetsToStore';
import { adaptResponseProductsToStore } from 'src/adapters/serviceResponseToStore/listings-api/adaptResponseProductsToStore';
import { adaptStoreToRequestBody } from 'src/adapters/storeToServiceRequest/listings-api/adaptStoreToRequestBody';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { RequestProductsActionPayload, TriggerName } from 'src/redux/products/types';
import { getListingsData } from 'src/services/listings-api/getListingsData';
import { ListingsAPIServingConfig } from 'src/services/listings-api/servingConfigs';
import { PRODUCTS_LIST_DATA_FETCHING_ERROR } from './thunkErrors';

export interface RequestProductsOptions {
  healthCheck?: boolean;
  triggerName?: TriggerName;
}

export const requestProducts = createAsyncThunk<
  RequestProductsActionPayload,
  RequestProductsOptions
>('products/requestProducts', async ({ healthCheck, triggerName }, { getState }) => {
  const state = getState() as ReduxApplicationState;

  let data: ListingsAPIResponseBody | null;

  if (healthCheck) {
    const { healthCheckResponseBody } = await import(
      'src/services/listings-api/healthCheckResponseBody'
    );
    data = healthCheckResponseBody;
  } else {
    data = await getListingsData({
      body: adaptStoreToRequestBody(state),
      userId: state.user.cognitoID || '',
      visitorId: state.userConfiguration.gaVisitorId,
      servingConfig: state.productsQuery.servingConfig,
    });
  }

  if (!data) {
    throw PRODUCTS_LIST_DATA_FETCHING_ERROR;
  }

  const useDynamicFacets =
    state.productsQuery.servingConfig === ListingsAPIServingConfig.SEARCH_DYNAMIC_FACETS;

  return {
    facets: useDynamicFacets
      ? adaptResponseDynamicFacetsToStore(data.facets, state.productsQuery.facets)
      : adaptResponseFacetsToStore(data.facets, state.productsQuery.facets),
    products: adaptResponseProductsToStore({
      products: data.products,
      pagination: data.pagination,
      attributionToken: data.metadata.attributionToken,
      term: state.productsQuery.searchTerm!,
      currencyCode: state.userConfiguration.currencyCode,
      deliveryZone: state.userConfiguration.deliveryZone!,
    }),
    triggerName,
  };
});
